import { useEffect, useMemo } from 'react';

import { useHomepageHeroExperiment } from 'experiments/use-homepage-hero-experiment';
import { IHomePageConfigurationFragment } from 'generated/sanity-graphql';
import { RunningExperiments, useExperiments } from 'state/experiments';

import {
  FeatureHomePageComponentTypes,
  FeatureHomePageComponents,
  IRawFeatureHomePageComponent,
} from '../types';

import { useAlert } from './use-alert';
import { useAppDownload } from './use-app-download';
import { useMarketingCardGroups } from './use-marketing-card-groups';
import { useMarketingTileGroups } from './use-marketing-tile-groups';
import { useRewardsSection } from './use-rewards-section';
import { useSimpleHomePage } from './use-simple-home-page';

const ID_ONLY_COMPONENTS = [
  FeatureHomePageComponentTypes.LockedOffers,
  FeatureHomePageComponentTypes.RecentItems,
  FeatureHomePageComponentTypes.QuestsSection,
];

const MULTIPLE_INSTANCE_COMPONENTS = [
  FeatureHomePageComponentTypes.MarketingCardGroup,
  FeatureHomePageComponentTypes.MarketingTileGroup,
  FeatureHomePageComponentTypes.SimpleHomePage,
];

/**
 * @name formatIdOnlyComponents
 * @description
 *  At this point there are some components we only care about their _id,
 *    and we don't need to query for them because we already have the _id
 *    from the original homepage query.
 *  So, lets just format them to mirror the queried components
 */
const formatIdOnlyComponents = (featureHomePageComponentMap: any) => {
  return ID_ONLY_COMPONENTS.reduce<
    Record<string, { _id: string; __typename: FeatureHomePageComponentTypes }>
  >((acc, compType) => {
    if (featureHomePageComponentMap[compType]) {
      acc[compType] = {
        _id: featureHomePageComponentMap[compType],
        __typename: compType,
      };
    }
    return acc;
  }, {});
};

const orderPopulatedFeatureHomePageComponents = (
  allPopulatedComponents: FeatureHomePageComponents,
  featureHomePage?: IHomePageConfigurationFragment,
  experimentSanityId?: string
) => {
  let componentsToIterate = featureHomePage?.components ?? [];

  /**
   * Experiment Injection Logic:
   * If an experiment is active, we manually inject its specific SimpleHomePage document directly.
   *
   * This allows us to:
   * 1. Test temporary/experimental content (e.g., 3 images) without manually adding these documents
   *    to the production "FeatureHomePage" configuration in the CMS.
   * 2. Ensure the iterator finds the correct ID (experimentSanityId) which we loaded separately,
   *    instead of looking for an ID that doesn't exist in the CMS list.
   */
  if (experimentSanityId) {
    const otherComponents = componentsToIterate.filter(
      c => c?.__typename !== FeatureHomePageComponentTypes.SimpleHomePage
    );
    componentsToIterate = [
      {
        __typename: FeatureHomePageComponentTypes.SimpleHomePage,
        _id: experimentSanityId,
        /**
         * Flag to identify this as the experiment component.
         * The experiment logic already confirmed this component should be shown for the expected daypart.
         * We inject this flag to bypass the HomePageView's redundant daypart check.
         * https://github.com/rbilabs/bk-us-app/blob/db203c1751f71811c256695c69601749efdb9b3a/src/components/features/home-page/home-page.view.tsx#L38
         */
        isExperimentComponent: true,
      } as IRawFeatureHomePageComponent,
      ...otherComponents,
    ];
  }

  return componentsToIterate.flatMap<FeatureHomePageComponents>(rawComp => {
    const typeName = rawComp?.__typename as FeatureHomePageComponentTypes;

    if (!typeName || !allPopulatedComponents[typeName]) {
      return [];
    }

    const isMultipleInstance = MULTIPLE_INSTANCE_COMPONENTS.includes(typeName);

    if (isMultipleInstance) {
      const specificItem = allPopulatedComponents[typeName].find(
        (popComp: any) => popComp._id === (rawComp as any)._id
      );

      if (!specificItem) {
        return [];
      }

      return (rawComp as any).isExperimentComponent
        ? [{ ...specificItem, isExperimentComponent: true }]
        : [specificItem];
    }

    return [allPopulatedComponents[typeName]];
  });
};

export const useFeatureHomePageComponents = (featureHomePage?: IHomePageConfigurationFragment) => {
  /**
   * @name featureHomePageComponentMap
   * @description
   *  maps raw feature home page components to { type: id } structure
   *  to simplify querying for the different component types
   */
  const featureHomePageComponentMap: FeatureHomePageComponents = useMemo(
    () =>
      (featureHomePage?.components ?? []).reduce<any>((acc, comp) => {
        if (!comp || !comp.__typename) {
          return acc;
        }
        const allowedMultipleInstances = MULTIPLE_INSTANCE_COMPONENTS.includes(
          comp.__typename as any
        );
        // These types are allowed multiple instances
        // set up the component type as an array of ids
        if (allowedMultipleInstances) {
          if (!acc[comp.__typename]) {
            acc[comp.__typename] = [];
          }
          acc[comp.__typename as FeatureHomePageComponentTypes].push((comp as any)._id);
        }
        // all others are limited to one instance so just one id
        else {
          acc[comp.__typename as FeatureHomePageComponentTypes] = (comp as any)._id;
        }
        return acc;
      }, {}),
    [featureHomePage?.components]
  );

  /**
   * ==================================
   *    INDIVIDUAL COMPONENT QUERIES
   * ==================================
   */
  const { experimentSanityId, isLoading: isExperimentLoading } = useHomepageHeroExperiment();
  const { logExposure } = useExperiments();

  const simpleHomePageIds = useMemo(() => {
    return experimentSanityId ? [experimentSanityId] : featureHomePageComponentMap.SimpleHomePage;
  }, [experimentSanityId, featureHomePageComponentMap.SimpleHomePage]);

  const { simpleHomePage, simpleHomePageLoading } = useSimpleHomePage(
    simpleHomePageIds,
    isExperimentLoading
  );

  useEffect(() => {
    if (simpleHomePage && experimentSanityId && !simpleHomePageLoading) {
      const hasExperimentComponent = simpleHomePage.some(c => c._id === experimentSanityId);
      if (hasExperimentComponent) {
        logExposure(RunningExperiments.HomepageHeroImage);
      }
    }
  }, [simpleHomePage, experimentSanityId, simpleHomePageLoading, logExposure]);

  const { alert } = useAlert(featureHomePageComponentMap.Alert);

  const { marketingCardGroups } = useMarketingCardGroups(
    featureHomePageComponentMap.MarketingCardGroup
  );

  const { marketingTileGroups } = useMarketingTileGroups(
    featureHomePageComponentMap.MarketingTileGroup
  );

  const { rewardsSection } = useRewardsSection(featureHomePageComponentMap.RewardsSection);

  const { appDownload } = useAppDownload(featureHomePageComponentMap.AppDownload);

  const idOnlyComponents = useMemo(() => formatIdOnlyComponents(featureHomePageComponentMap), [
    featureHomePageComponentMap,
  ]);

  /**
   * ==================================
   *   ORDER POPULATED COMPONENT DATA
   * ==================================
   */
  const orderedPopulatedFeatureHomePageComponents = useMemo(() => {
    const allPopulatedComponents: FeatureHomePageComponents = {
      [FeatureHomePageComponentTypes.Alert]: alert,
      [FeatureHomePageComponentTypes.MarketingTileGroup]: marketingTileGroups,
      [FeatureHomePageComponentTypes.MarketingCardGroup]: marketingCardGroups,
      [FeatureHomePageComponentTypes.RewardsSection]: rewardsSection,
      [FeatureHomePageComponentTypes.AppDownload]: appDownload,
      [FeatureHomePageComponentTypes.SimpleHomePage]: simpleHomePage,
      ...idOnlyComponents,
    };
    return orderPopulatedFeatureHomePageComponents(
      allPopulatedComponents,
      featureHomePage,
      experimentSanityId
    );
  }, [
    alert,
    appDownload,
    featureHomePage,
    idOnlyComponents,
    marketingCardGroups,
    marketingTileGroups,
    rewardsSection,
    simpleHomePage,
    experimentSanityId,
  ]);

  /**
   * ==================================
   *            RETURN DATA
   * ==================================
   */
  return {
    featureHomePageComponentsLoading: simpleHomePageLoading,
    featureHomePageComponents: orderedPopulatedFeatureHomePageComponents,
  };
};
